import {
  AttendanceExportContext,
  buildExportMetaLines,
  buildStudentAttendanceExportPayload,
  formatAttendanceModeLabel,
} from './attendanceExportBuilder'
import {buildExportNarrativeLines, buildExportNoDataLines} from './attendanceExportFigures'
import {AttendanceReportExportPayload} from './attendanceReportExportTypes'
import {
  columnsForV2Intent,
  getV2ResultTableData,
  v2IndividualToParsedContent,
} from './attendanceV2ResultTable'
import {
  AttendanceV2Payload,
  getReportPeriodLabel,
  markTypeToAttendanceMode,
} from './attendanceV2Types'
import {AttendanceDisplayMode} from './attendanceWizardTypes'
import {AttendanceVisualSummary} from './buildAttendanceVisualSummary'
import {buildV2AttendanceVisualSummary} from './buildV2VisualSummary'
import {formatAttendanceExportSummaryLines} from './formatAttendanceExportSummary'
import {AttendanceParsedContent} from './parseAttendanceAnswer'
import {renderAttendanceSummaryChartPng} from './renderAttendanceSummaryChartPng'
import {getAttendanceModeLabelId, normalizeAttendanceMode} from './resolveAttendanceDisplayMode'

function resolvePersonFromScope(scope: Record<string, unknown> | undefined) {
  const resolved = scope?.resolvedPersons
  if (!Array.isArray(resolved) || resolved.length === 0) return null
  const person = resolved[0]
  if (!person || typeof person !== 'object') return null
  return person as Record<string, unknown>
}

/** The mark unit the answer actually measured. `markType` is the backend's own account of it, so it
 *  outranks `querySpec.attendanceMode` — which, for the payloads the SQL agent sends, is a value the
 *  portal synthesised rather than anything the data said. The tenant's `subject_attendance` setting
 *  stays as the last fallback, via `normalizeAttendanceMode`, for payloads that state neither. */
export function resolveV2AttendanceMode(payload: AttendanceV2Payload): AttendanceDisplayMode {
  return normalizeAttendanceMode(
    markTypeToAttendanceMode(payload.data?.markType),
    normalizeAttendanceMode(payload.querySpec?.attendanceMode)
  )
}

export function resolveV2ExportContext(payload: AttendanceV2Payload): AttendanceExportContext {
  const summary = payload.data?.summary
  const scope = payload.querySpec?.scope as Record<string, unknown> | undefined
  const person = resolvePersonFromScope(scope)
  const firstRow = payload.data?.rows?.[0]

  return {
    studentId:
      person?.studentId != null
        ? String(person.studentId)
        : firstRow?.studentId != null
          ? String(firstRow.studentId)
          : undefined,
    studentName:
      summary?.personName?.trim() ||
      (typeof person?.studentName === 'string'
        ? person.studentName
        : typeof person?.name === 'string'
          ? person.name
          : typeof firstRow?.studentName === 'string'
            ? firstRow.studentName
            : undefined),
    branch:
      (typeof person?.branch === 'string' ? person.branch : undefined) ||
      (typeof firstRow?.branch === 'string' ? firstRow.branch : undefined),
    className:
      (typeof person?.class === 'string'
        ? person.class
        : typeof person?.className === 'string'
          ? person.className
          : undefined) ||
      (typeof firstRow?.className === 'string' ? firstRow.className : undefined),
    section:
      (typeof person?.section === 'string' ? person.section : undefined) ||
      (typeof firstRow?.section === 'string' ? firstRow.section : undefined),
    // `data.summary` only exists on legacy payloads; the SQL agent sends `data.period`, bounded by
    // the rows actually returned. Without this the "Date range" line never rendered at all.
    startDate: payload.data?.period?.from ?? summary?.dateFrom,
    endDate: payload.data?.period?.to ?? summary?.dateTo,
    periodLabel: getReportPeriodLabel(payload) ?? undefined,
    // The scope sentence the backend composed. An exported PDF is read away from the chat that
    // qualified it, so this is the only place the reader learns whether "the five best classes"
    // means the school or one campus.
    scopeLabel: payload.data?.scope?.label?.trim() || undefined,
  }
}

function buildGenericTableExportPayload(options: {
  title: string
  filename: string
  ctx: AttendanceExportContext
  columns: string[]
  rows: Record<string, string>[]
  summary?: AttendanceVisualSummary | null
}): AttendanceReportExportPayload | null {
  const {title, filename, ctx, columns, rows, summary} = options
  if (!columns.length || !rows.length) return null

  return {
    title,
    metaLines: buildExportMetaLines(ctx),
    summaryLines: formatAttendanceExportSummaryLines(summary),
    chartImageDataUrl: renderAttendanceSummaryChartPng(summary),
    columns,
    rows,
    filename,
  }
}

function tableRowsToExportRows(
  columns: ReturnType<typeof columnsForV2Intent>,
  rows: Record<string, unknown>[]
): {columns: string[]; rows: Record<string, string>[]} {
  const exportColumns = columns.map((col) => col.label)
  const exportRows = rows.map((row) => {
    const out: Record<string, string> = {}
    for (const col of columns) {
      const value = row[col.key]
      out[col.label] = value == null || value === '' ? '' : String(value)
    }
    return out
  })
  return {columns: exportColumns, rows: exportRows}
}

function buildExportFilename(ctx: AttendanceExportContext, fallback: string): string {
  const base = ctx.studentName?.trim().replace(/\s+/g, '-') || fallback
  return base.replace(/[^a-zA-Z0-9-_]+/g, '-').replace(/-+/g, '-')
}

export function buildV2ResultExportPayload(
  payload: AttendanceV2Payload,
  options: {
    defaultTitle: string
    attendanceModeLabel?: string
    heading?: string
  }
): AttendanceReportExportPayload | null {
  // The export keeps a packed names cell as one cell: a CSV column of fifty names is still every
  // name, whereas the on-screen table hands them to a list of their own.
  const {intent, columns, rows} = getV2ResultTableData(payload, {splitNameLists: false})
  if (!columns.length || !rows.length) return null

  const ctx = resolveV2ExportContext(payload)
  const ctxWithMode = {...ctx, attendanceModeLabel: options.attendanceModeLabel}
  const visualSummary = buildV2AttendanceVisualSummary(payload)
  const title =
    ctx.studentName?.trim() || payload.data?.summary?.personName?.trim() || options.defaultTitle
  const filename = buildExportFilename(ctx, `attendance-${intent}`)
  const chatOrder = {
    narrativeLines: buildExportNarrativeLines(payload),
    heading: options.heading?.trim() || undefined,
    footerLines: buildExportNoDataLines(payload),
  }

  if (intent === 'individual') {
    const parsed = v2IndividualToParsedContent(payload)
    const content: AttendanceParsedContent = {
      kind: 'daily-records',
      summary: '',
      columns: parsed.columns,
      rows: parsed.rows,
      confidence: 1,
    }
    if (!content.columns.length || !content.rows.length) return null

    const individual = buildStudentAttendanceExportPayload({
      title,
      filename,
      ctx: ctxWithMode,
      content,
      summary: visualSummary,
      attendanceMode: resolveV2AttendanceMode(payload),
    })
    return individual ? {...individual, ...chatOrder} : null
  }

  const table = tableRowsToExportRows(columns, rows)
  const generic = buildGenericTableExportPayload({
    title,
    filename,
    ctx: ctxWithMode,
    columns: table.columns,
    rows: table.rows,
    summary: visualSummary,
  })
  return generic ? {...generic, ...chatOrder} : null
}

export function resolveV2AttendanceModeLabel(
  payload: AttendanceV2Payload,
  formatMessage: (descriptor: {id: string}) => string
): string | undefined {
  return formatAttendanceModeLabel(
    resolveV2AttendanceMode(payload),
    formatMessage,
    getAttendanceModeLabelId
  )
}
